import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { styled } from "@mui/material";
import {
  LineChart,
  lineElementClasses,
  markElementClasses,
  areaElementClasses,
} from "@mui/x-charts/LineChart";

import { useAppContext } from "../context/AppContext";
import { Datapoint, useVisualisationData } from "../hooks/useVisualisationData";
import { Patient } from "../models/patient";
import ChartPopover from "./ChartPopover";
import { useCalculatedChartData } from "../hooks/useCalculatedChartData";
import { useChart } from "../hooks/useChart";
import { chartClasses } from "../utils/chartUtils";

const Root = styled(LineChart)({
  [`& :has(.${chartClasses.highlighted})`]: {
    cursor: "pointer",
  },
  [`& .${lineElementClasses.root}.${chartClasses.highlighted}`]: {
    strokeWidth: 2,
    opacity: 1,
  },
  [`& .${lineElementClasses.root}.${chartClasses.dimmed}`]: {
    opacity: 0.15,
  },
  [`& .${markElementClasses.root}`]: {
    strokeWidth: 1.5,
  },
  [`& .${markElementClasses.root}.${chartClasses.highlighted}`]: {
    scale: "1",
    strokeWidth: 1.5,
    opacity: 1,
  },
  [`& .${markElementClasses.root}.${chartClasses.dimmed}`]: {
    opacity: 0.15,
  },
});

const Chart: React.FC<{
  patient: Patient;
  timeframe: { from?: Date | null; to?: Date | null };
  showNight: boolean;
  showMidnight: boolean;
  setExtrasAvailable: Dispatch<SetStateAction<boolean>>;
}> = ({ patient, timeframe, showNight, showMidnight, setExtrasAvailable }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const ref = useRef<SVGSVGElement>(null);

  const { legend, setLegend, dataConfig, chartConfig } = useAppContext();
  const { dataset, series, activities } = useVisualisationData({
    exportId: dataConfig.exportId || undefined,
    patientId: patient.id,
    timeframe,
  });

  const { chartData, chartSeries } = useCalculatedChartData(
    series,
    legend,
    showNight,
    showMidnight,
    dataset
  );

  const hasExperiencesOrActivities = (dataset: Datapoint[]) => {
    return dataset.some((item) => {
      return (
        (item.extras.experiences && item.extras.experiences.length > 0) ||
        (item.extras.positiveActivities &&
          item.extras.positiveActivities.length > 0)
      );
    });
  };

  const { axisProps } = useChart(ref, legend, setLegend);

  useEffect(() => {
    const hasExtras = hasExperiencesOrActivities(dataset);
    setExtrasAvailable(hasExtras);
  }, [dataset, setExtrasAvailable]);

  return (
    <Root
      key={`${timeframe.from?.getTime()}-${timeframe.to?.getTime()}`}
      ref={ref}
      grid={{ vertical: false, horizontal: true }}
      yAxis={[{ ...axisProps.y, min: -1, max: 10 }]}
      xAxis={[
        {
          scaleType: chartConfig.scale,
          ...axisProps.x,
          dataKey: "timestamp",
          valueFormatter: (value) => {
            return new Date(value).toLocaleDateString(currentLanguage, {
              day: "2-digit",
              month: "short",
              year: "numeric",
            });
          },
        },
      ]}
      series={chartSeries}
      dataset={chartData}
      height={400}
      tooltip={{ trigger: "item" }}
      margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
      slots={{
        legend: () => null,
        itemContent: (props) => (
          <ChartPopover
            dataIndex={props.itemData.dataIndex}
            data={chartData}
            series={series}
            dataset={dataset}
            activities={activities}
          />
        ),
      }}
      slotProps={{
        loadingOverlay: { message: `${t("common.loading")}` },
        noDataOverlay: { message: `${t("tabs.chart.no-data")}` },
      }}
      sx={{
        [`& .${areaElementClasses.root}`]: {
          opacity: 0.2,
        },
        [`& .${markElementClasses.root}:hover`]: {
          cursor: "pointer",
        },
        "& .MuiLineElement-series-night, .MuiLineElement-series-extras": {
          strokeWidth: "0 !important",
        },
      }}
    />
  );
};

export default Chart;
